import { includes } from 'lodash';
import { useEffect, useState } from 'react';

import { toastError } from 'common/toast';
import {
  logoutUser,
  setCsrfToken,
  loginUserPass,
  fetchIsUserAuthenticated,
} from 'api/auth';

import { UserRole } from 'common/enum/user-role.enum';
import { SystemUser } from 'common/types/system-users';
import { LoginStateFields } from 'common/enum/login-state-fields.enum';

export const useAuthenticatedUser = () => {
  const [user, setUser] = useState<SystemUser>();
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [isLoginLoading, setIsLoginLoading] = useState<boolean>(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  useEffect(() => {
    fetch();
  }, []);

  const fetch = async (): Promise<void> => {
    try {
      await setCsrfToken();
      const user: SystemUser & { customer_name: string } =
        await fetchIsUserAuthenticated();

      setIsLoaded(true);
      setIsAuthenticated(true);
      setUser(user);

      if (user.username !== UserRole.ROOT) {
        window['pendo']?.identify?.({
          visitor: {
            roles: user.roles,
            email: user.username,
            username: user.first_name,
            id: `${user.customer_name} - ${user.username}`,
          },
          account: {
            id: `suridata - ${user.customer_name}`,
          },
        });
      }
    } catch (error) {
      setIsLoaded(true);
      setUser(undefined);
      setIsAuthenticated(false);
    }
  };

  const login = async ({
    username,
    password,
    tenantName,
    isRootLogin,
    isFortiCloudMode,
  }: {
    username: string;
    password: string;
    tenantName: string;
    isRootLogin: boolean;
    isFortiCloudMode: boolean;
  }): Promise<void> => {
    try {
      setIsLoginLoading(true);

      const loginParams = {
        username,
        password,
        tenantName: undefined,
      };

      if (isFortiCloudMode && isRootLogin) {
        if (tenantName === null) {
          throw new Error('MissingTenantName', {
            cause: { missingTenantName: LoginStateFields.TENANT_NAME },
          });
        } else {
          loginParams.tenantName = tenantName;
        }
      }

      const user: SystemUser & { customer_name: string } = await loginUserPass(
        loginParams,
      );

      setIsLoaded(true);
      setIsAuthenticated(true);

      setUser(user);
    } catch (error) {
      const errorMessage =
        error.message === 'MissingTenantName'
          ? `Missing query param: ${error.cause.missingTenantName}`
          : 'Login failed. username/password is incorrect';

      toastError(errorMessage);
    } finally {
      setIsLoginLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await logoutUser();
    } finally {
      setIsLoaded(true);
      setUser(undefined);
      setIsAuthenticated(false);
    }
  };

  const hasRole = (role: string) => includes(user.roles, role);

  const hasPermission = (permission: string): boolean =>
    includes(user?.permissions, permission);

  const isRoot = user?.username == UserRole.ROOT;
  const isAdmin =
    includes(user?.roles, UserRole.ADMIN) ||
    includes(user?.roles, UserRole.OWNER);

  return {
    user,
    login,
    logout,
    isRoot,
    isAdmin,
    hasRole,
    isLoaded,
    hasPermission,
    refetch: fetch,
    isLoginLoading,
    isAuthenticated,
  };
};
